import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, homePath } from "@/lib/auth.jsx";
import { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";
import { ArrowRight, Eye, EyeOff } from "lucide-react";

export default function ResetPassword() {
  const { resetPassword } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    setErr("");
    if (password !== confirm) {
      setErr("Passwords do not match");
      return;
    }
    if (password.length < 8) {
      setErr("Password must be at least 8 characters");
      return;
    }
    setLoading(true);
    try {
      const u = await resetPassword(token, password);
      navigate(homePath(u), { replace: true });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-white">
      <header className="px-6 sm:px-12 py-6 flex items-center justify-between border-b border-neutral-100">
        <Logo />
        <Link to="/login" data-testid="reset-back-login" className="text-sm text-propera font-semibold hover:underline">Back to Sign In</Link>
      </header>
      <div className="max-w-md mx-auto px-6 py-16">
        <p className="text-xs uppercase tracking-[0.2em] text-propera font-semibold mb-3">Reset Password</p>
        <h1 className="text-3xl sm:text-4xl font-bold text-neutral-900 leading-tight">Set a new password</h1>
        <p className="text-neutral-500 mt-3">Choose something strong — at least 8 characters.</p>
        <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="reset-form">
          <div>
            <label className="text-xs font-semibold text-neutral-700">New Password</label>
            <div className="relative mt-1.5">
              <input
                data-testid="reset-password"
                type={show ? "text" : "password"}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full px-4 py-3 rounded-xl border border-neutral-200 focus:border-propera focus:ring-2 focus:ring-propera/15 outline-none transition pr-12"
              />
              <button type="button" onClick={() => setShow((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-700">
                {show ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-neutral-700">Confirm Password</label>
            <input
              data-testid="reset-confirm"
              type={show ? "text" : "password"}
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="••••••••"
              className="mt-1.5 w-full px-4 py-3 rounded-xl border border-neutral-200 focus:border-propera focus:ring-2 focus:ring-propera/15 outline-none transition"
            />
          </div>
          {err && (
            <div data-testid="reset-error" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2">{err}</div>
          )}
          <button
            data-testid="reset-submit"
            type="submit"
            disabled={loading}
            className="w-full bg-propera hover:bg-propera-hover text-white rounded-full px-6 py-3.5 font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-60 glow-primary"
          >
            {loading ? "Updating..." : (<>Update Password <ArrowRight size={16} /></>)}
          </button>
        </form>
      </div>
    </div>
  );
}
